/**
 * NavigatorStatusComponent
 *
 * Shown on the workorders page below the event status badge when a navigator
 * has been started (event.navigatorCurrentStep is set and not 'started').
 *
 * Displays:
 * - IN PROGRESS badge
 * - Current step name + elapsed vs max time for current step (colour-coded)
 * - Overall elapsed vs total max time + variance
 * - Inline editable actual durations for completed steps
 *
 * Clicking the badge / current step opens the Update Navigator dialog.
 * Real-time: reacts to live subscription updates via selectJob.
 */
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { WorkOrdersActions } from '../job-workorders.actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StepTiming {
    id: string;
    title: string;
    maxTime: number;           // seconds
    startedAt: number | null;  // epoch ms
    completedAt: number | null;// epoch ms
    /** Actual duration in seconds (if completed, or elapsed if current) */
    actualSeconds: number | null;
    isCurrent: boolean;
    isCompleted: boolean;
    /** True if completed step ran over maxTime */
    isOver: boolean;
    overBySeconds: number;
}

export interface NavigatorStatus {
    currentStepId: string;
    currentStepTitle: string;
    currentStepMaxSeconds: number;
    currentStepElapsedSeconds: number;
    currentStepIsOver: boolean;
    currentStepOverBySeconds: number;
    /** Sum of maxTime for all steps */
    totalMaxSeconds: number;
    /** Sum of actual durations for completed steps + elapsed for current */
    totalElapsedSeconds: number;
    totalIsOver: boolean;
    totalOverBySeconds: number;
    steps: StepTiming[];
    /** Projected total duration in seconds */
    projectedTotalSeconds: number;
    /** projectedTotal - totalMax: positive = behind, negative = ahead */
    projectedDeltaSeconds: number;
    /** Actual clock time (epoch ms) when the job is projected to finish */
    projectedFinishTime: number;
}

// ─── Step definitions ─────────────────────────────────────────────────────────
// Steps are read dynamically from navigatorData.steps (written by the
// navigator effect as each step is confirmed). This means the table always
// reflects the actual navigator being used — v1, v2, or any custom script.
//
// Fallback: if navigatorData has no steps yet, we fall back to the v2 step
// list so the ops team sees something meaningful from the first step.

const FALLBACK_STEPS: { id: string; title: string; defaultMaxTime: number }[] = [
    { id: 'confirm_job',             title: 'Confirm Job Details',             defaultMaxTime: 300 },
    { id: 'crew_checkin',            title: 'Crew Check-In',                   defaultMaxTime: 300 },
    { id: 'drive_to_start',          title: 'Drive to Start Location',         defaultMaxTime: 600 },
    { id: 'arrival_start',           title: 'Arrival — Intro & Walk Through',  defaultMaxTime: 900 },
    { id: 'walkthrough_scope',       title: 'Confirm Scope',                   defaultMaxTime: 900 },
    { id: 'contract_sign',           title: 'Contract & Pre-Authorization',    defaultMaxTime: 600 },
    { id: 'home_protection_load',    title: 'Protect the Home',               defaultMaxTime: 1500 },
    { id: 'load',                    title: 'Pad, Protect & Load',            defaultMaxTime: 3600 },
    { id: 'load_complete',           title: 'Final Load Check',               defaultMaxTime: 600 },
    { id: 'drive_to_end',            title: 'Drive to End Location',          defaultMaxTime: 1200 },
    { id: 'arrival_end',             title: 'Arrival — Park & Walk Through',  defaultMaxTime: 600 },
    { id: 'home_protection_unload',  title: 'Protect the New Home',           defaultMaxTime: 1200 },
    { id: 'unload',                  title: 'Unload, Unwrap & Arrange',       defaultMaxTime: 2700 },
    { id: 'final_walkthrough',       title: 'Final Walk Through',             defaultMaxTime: 900 },
    { id: 'star_rating',             title: '5-Star Service Check',           defaultMaxTime: 300 },
    { id: 'collect_payment',         title: 'Collect Payment',                defaultMaxTime: 600 },
    { id: 'move_complete',           title: 'Move Complete',                  defaultMaxTime: 600 },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseNavigatorData(raw: string | null | undefined): Record<string, any> {
    try { return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}

function formatDuration(seconds: number): string {
    if (seconds < 0) seconds = 0;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
    selector: 'app-navigator-status',
    standalone: true,
    imports: [CommonModule, TooltipModule, ButtonModule],
    styles: [`
        :host { display: block; }

        .nav-status-bar {
            display: flex; align-items: center; gap: 0.5rem;
            flex-wrap: wrap;
            cursor: pointer;
        }
        .badge-inprogress {
            display: inline-flex; align-items: center; gap: 0.35rem;
            background: #1d6a2e; color: white;
            font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em;
            text-transform: uppercase;
            padding: 0.2rem 0.55rem; border-radius: 10px;
            cursor: pointer; flex-shrink: 0;
        }
        .badge-inprogress i { font-size: 0.65rem; }
        .badge-completed {
            display: inline-flex; align-items: center; gap: 0.35rem;
            background: #1a5276; color: white;
            font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em;
            text-transform: uppercase;
            padding: 0.2rem 0.55rem; border-radius: 10px;
            flex-shrink: 0;
        }
        .step-pill {
            display: inline-flex; align-items: center; gap: 0.3rem;
            background: #e8f5e9; color: #1d6a2e;
            font-size: 0.78rem; font-weight: 600;
            padding: 0.2rem 0.55rem; border-radius: 10px;
        }
        .step-pill.over { background: #fdecea; color: #c0392b; }
        .timing-chip {
            display: inline-flex; align-items: center;
            background: #f0f0f0; color: #444;
            font-size: 0.75rem;
            padding: 0.2rem 0.55rem; border-radius: 10px;
        }
        .timing-chip.over { background: #fdecea; color: #c0392b; }
        .timing-chip.ok { background: #e8f5e9; color: #1d6a2e; }

        /* Step timing table */
        .step-timings {
            margin-top: 0.5rem;
            border: 1px solid #e0e0e0; border-radius: 6px;
            overflow: hidden;
            font-size: 0.82rem;
        }
        .step-row {
            display: grid;
            grid-template-columns: 1fr 70px 70px 70px;
            align-items: center;
            gap: 0.25rem;
            padding: 0.35rem 0.6rem;
            border-bottom: 1px solid #f0f0f0;
        }
        .step-row:last-child { border-bottom: none; }
        .step-row.current { background: #f0f7f0; }
        .step-row.over .step-actual { color: #c0392b; font-weight: 600; }
        .step-row.ok .step-actual { color: #1d6a2e; }
        .col-header { font-weight: 700; color: #888; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; }
        .step-name { color: #222; }
        .step-name.current-name { font-weight: 600; }
        .step-max { color: #888; text-align: right; }
        .step-actual { text-align: right; }
        .step-diff { text-align: right; font-size: 0.75rem; }
        .over-text { color: #c0392b; }
        .ok-text { color: #1d6a2e; }
        .pending-text { color: #aaa; }
        /* Completed steps are clickable — opens dialog for editing */
        .step-row.is-clickable { cursor: pointer; }
        .step-row.is-clickable:hover { background: #f5f5f5; }
        .edit-hint { color: #bbb; font-size: 0.7rem; }
        .total-row {
            display: grid;
            grid-template-columns: 1fr 70px 70px 70px;
            gap: 0.25rem;
            padding: 0.4rem 0.6rem;
            background: #f8f8f8;
            font-weight: 700;
            font-size: 0.82rem;
            border-top: 2px solid #e0e0e0;
        }
        .projection-row {
            display: flex; align-items: center; gap: 0.5rem;
            padding: 0.4rem 0.6rem;
            font-size: 0.8rem;
            border-top: 1px dashed #e0e0e0;
            background: #fafafa;
        }
        .projection-label { color: #888; }
        .projection-value { font-weight: 600; }
        .projection-on-track { color: #1d6a2e; }
        .projection-behind  { color: #c0392b; }
        .projection-ahead   { color: #1d6a2e; }
        .projection-dot {
            width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        }
        .dot-green  { background: #1d6a2e; }
        .dot-red    { background: #c0392b; }
        .dot-yellow { background: #f59e0b; }
    `],
    template: `
        <ng-container *ngIf="status">
            <!-- Compact summary bar -->
            <div class="nav-status-bar" (click)="openDialog()">
                <!-- Completed state -->
                <ng-container *ngIf="isCompleted">
                    <span class="badge-completed">
                        <i class="pi pi-check-circle"></i>
                        Completed
                    </span>
                    <span class="timing-chip ok"
                          [pTooltip]="'Total actual vs scheduled'">
                        {{ formatDuration(status.totalElapsedSeconds) }}
                        / {{ formatDuration(status.totalMaxSeconds) }}
                        <span *ngIf="status.totalIsOver" class="ml-1">
                            (+{{ formatDuration(status.totalOverBySeconds) }})
                        </span>
                    </span>
                </ng-container>

                <!-- In progress state -->
                <ng-container *ngIf="!isCompleted">
                    <span class="badge-inprogress">
                        <i class="pi pi-circle-fill"></i>
                        In Progress
                    </span>
                    <span class="step-pill" [class.over]="status.currentStepIsOver"
                          [pTooltip]="'Current step: ' + status.currentStepTitle">
                        Step {{ currentStepIndex + 1 }}: {{ status.currentStepTitle }}
                    </span>
                    <span class="timing-chip" [class.over]="status.currentStepIsOver"
                          [class.ok]="!status.currentStepIsOver"
                          [pTooltip]="'Step elapsed vs max'">
                        {{ formatDuration(status.currentStepElapsedSeconds) }}
                        / {{ formatDuration(status.currentStepMaxSeconds) }}
                        <span *ngIf="status.currentStepIsOver" class="ml-1">
                            (+{{ formatDuration(status.currentStepOverBySeconds) }})
                        </span>
                    </span>
                    <span class="timing-chip" [class.over]="status.totalIsOver"
                          [class.ok]="!status.totalIsOver"
                          [pTooltip]="'Total elapsed vs total max'">
                        Total: {{ formatDuration(status.totalElapsedSeconds) }}
                        / {{ formatDuration(status.totalMaxSeconds) }}
                        <span *ngIf="status.totalIsOver" class="ml-1">
                            (+{{ formatDuration(status.totalOverBySeconds) }})
                        </span>
                    </span>
                </ng-container>
            </div>

            <!-- Expandable step timing table -->
            <div class="step-timings" *ngIf="showTimings">
                <!-- Header -->
                <div class="step-row">
                    <span class="col-header">Step</span>
                    <span class="col-header" style="text-align:right">Max</span>
                    <span class="col-header" style="text-align:right">Actual</span>
                    <span class="col-header" style="text-align:right">+/-</span>
                </div>
                <div *ngFor="let step of status.steps"
                     class="step-row"
                     [class.current]="step.isCurrent"
                     [class.over]="step.isOver && step.isCompleted"
                     [class.ok]="!step.isOver && step.isCompleted"
                     [class.is-clickable]="step.isCompleted"
                     (click)="step.isCompleted ? openDialogForStep(step.id) : null"
                     [pTooltip]="step.isCompleted ? 'Click to edit duration' : null">
                    <!-- Step name -->
                    <span class="step-name" [class.current-name]="step.isCurrent">
                        {{ step.title }}
                        <i *ngIf="step.isCurrent" class="pi pi-arrow-left ml-1" style="font-size:0.7rem;color:#1d6a2e"></i>
                        <i *ngIf="step.isCompleted" class="pi pi-pencil ml-1 edit-hint"></i>
                    </span>
                    <!-- Max time -->
                    <span class="step-max">{{ formatDuration(step.maxTime) }}</span>
                    <!-- Actual time -->
                    <span class="step-actual">
                        <span *ngIf="step.actualSeconds !== null">{{ formatDuration(step.actualSeconds) }}</span>
                        <span *ngIf="step.actualSeconds === null" class="pending-text">—</span>
                    </span>
                    <!-- Diff -->
                    <span class="step-diff">
                        <ng-container *ngIf="step.isCompleted && step.actualSeconds !== null">
                            <span [class]="step.isOver ? 'over-text' : 'ok-text'">
                                {{ step.isOver ? '+' : '-' }}{{ formatDuration(Math.abs(step.actualSeconds - step.maxTime)) }}
                            </span>
                        </ng-container>
                        <span *ngIf="!step.isCompleted" class="pending-text">—</span>
                    </span>
                </div>
                <!-- Total row — actual so far vs scheduled -->
                <div class="total-row">
                    <span>{{ isCompleted ? 'Total' : 'Total so far' }}</span>
                    <span style="text-align:right">{{ formatDuration(status.totalMaxSeconds) }}</span>
                    <span style="text-align:right" [class]="status.totalIsOver ? 'over-text' : 'ok-text'">
                        {{ formatDuration(status.totalElapsedSeconds) }}
                    </span>
                    <span style="text-align:right" [class]="status.totalIsOver ? 'over-text' : 'ok-text'">
                        {{ status.totalIsOver ? '+' : '-' }}{{
                            formatDuration(Math.abs(status.totalElapsedSeconds - status.totalMaxSeconds))
                        }}
                    </span>
                </div>

                <!-- Projection row — only shown when still in progress -->
                <div class="projection-row" *ngIf="!isCompleted">
                    <span class="projection-dot"
                          [class.dot-green]="status.projectedDeltaSeconds <= 0"
                          [class.dot-yellow]="status.projectedDeltaSeconds > 0 && status.projectedDeltaSeconds <= 300"
                          [class.dot-red]="status.projectedDeltaSeconds > 300">
                    </span>
                    <span class="projection-label">Done ~</span>
                    <span class="projection-value"
                          [class.projection-on-track]="status.projectedDeltaSeconds <= 0"
                          [class.projection-behind]="status.projectedDeltaSeconds > 0"
                          [pTooltip]="formatDuration(status.projectedTotalSeconds) + ' total'">
                        {{ formatClockTime(status.projectedFinishTime) }}
                    </span>
                    <span class="projection-value ml-1"
                          [class.projection-on-track]="status.projectedDeltaSeconds <= 0"
                          [class.projection-ahead]="status.projectedDeltaSeconds < 0"
                          [class.projection-behind]="status.projectedDeltaSeconds > 0">
                        <ng-container *ngIf="status.projectedDeltaSeconds === 0">(on time)</ng-container>
                        <ng-container *ngIf="status.projectedDeltaSeconds > 0">
                            (+{{ formatDuration(status.projectedDeltaSeconds) }} behind)
                        </ng-container>
                        <ng-container *ngIf="status.projectedDeltaSeconds < 0">
                            ({{ formatDuration(Math.abs(status.projectedDeltaSeconds)) }} ahead)
                        </ng-container>
                    </span>
                </div>
            </div>

            <div style="margin-top:0.25rem">
                <button style="font-size:0.75rem;color:#888;background:none;border:none;cursor:pointer;padding:0"
                        (click)="showTimings = !showTimings">
                    {{ showTimings ? '▲ Hide step timings' : '▼ Show step timings' }}
                </button>
            </div>
        </ng-container>
    `,
})
export class NavigatorStatusComponent implements OnInit, OnChanges, OnDestroy {
    @Input() eventId: string;
    @Input() navigatorCurrentStep: string | null;
    @Input() navigatorData: string | null;

    status: NavigatorStatus | null = null;
    showTimings = false;

    private destroy$ = new Subject<void>();
    private navData: Record<string, any> = {};

    constructor(private store: Store) {}

    ngOnInit() {
        // Tick every second to update elapsed time — stop when completed
        interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (!this.isCompleted) this._computeStatus();
        });
        this._computeStatus();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['navigatorCurrentStep'] || changes['navigatorData']) {
            this.navData = parseNavigatorData(this.navigatorData);
            this._computeStatus();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    readonly Math = Math;

    get isCompleted(): boolean {
        return this.navigatorCurrentStep === 'completed';
    }

    get currentStepIndex(): number {
        return this._getWorkSteps().findIndex(s => s.id === this.navigatorCurrentStep);
    }

    /**
     * Build the step list from navigatorData.steps (dynamic) falling back to
     * FALLBACK_STEPS if the navigator hasn't started writing step data yet.
     */
    private _getWorkSteps(): { id: string; title: string; defaultMaxTime: number }[] {
        const stepsData: Record<string, any> = this.navData.steps ?? {};
        const knownIds = Object.keys(stepsData);

        if (knownIds.length === 0) {
            // Navigator hasn't written any step data yet — use fallback list
            // but only include up to and including the current step
            const currentIdx = FALLBACK_STEPS.findIndex(s => s.id === this.navigatorCurrentStep);
            if (currentIdx < 0) return FALLBACK_STEPS;
            return FALLBACK_STEPS;
        }

        // Build ordered list from steps written to navigatorData.
        // The step data preserves insertion order (JS objects maintain insertion
        // order for string keys in modern engines). Each entry has startedAt/completedAt.
        const dynamicSteps = knownIds.map(id => {
            const fallback = FALLBACK_STEPS.find(f => f.id === id);
            return {
                id,
                title: fallback?.title ?? id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                defaultMaxTime: fallback?.defaultMaxTime ?? 0,
            };
        });

        // If the current step isn't in dynamicSteps yet (just advanced to it
        // but step data hasn't been written), append it
        if (this.navigatorCurrentStep
            && this.navigatorCurrentStep !== 'completed'
            && this.navigatorCurrentStep !== 'started'
            && !dynamicSteps.find(s => s.id === this.navigatorCurrentStep)) {
            const fallback = FALLBACK_STEPS.find(f => f.id === this.navigatorCurrentStep);
            dynamicSteps.push({
                id: this.navigatorCurrentStep,
                title: fallback?.title ?? this.navigatorCurrentStep.replace(/_/g, ' '),
                defaultMaxTime: fallback?.defaultMaxTime ?? 0,
            });
        }

        return dynamicSteps;
    }

    openDialog() {
        this.store.dispatch(WorkOrdersActions.updateNavigatorMenuItemClicked({ eventId: this.eventId }));
    }

    /** Open the dialog with a specific completed step pre-selected for editing. */
    openDialogForStep(stepId: string) {
        this.store.dispatch(WorkOrdersActions.updateNavigatorMenuItemClicked({
            eventId: this.eventId,
            initialStepId: stepId,
        }));
    }

    formatClockTime(epochMs: number): string {
        if (!epochMs) return '—';
        return new Date(epochMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }



    // ── Status computation ────────────────────────────────────────────────────

    private _computeStatus() {
        if (!this.navigatorCurrentStep || this.navigatorCurrentStep === 'started') {
            this.status = null;
            return;
        }

        const stepsData: Record<string, any> = this.navData.steps ?? {};

        const now = Date.now();
        const jobDone = this.navigatorCurrentStep === 'completed';
        const workSteps = this._getWorkSteps();
        const currentIdx = jobDone
            ? workSteps.length // past all work steps
            : workSteps.findIndex(s => s.id === this.navigatorCurrentStep);
        if (currentIdx < 0 && !jobDone) { this.status = null; return; }

        const stepTimings: StepTiming[] = workSteps.map((def, i) => {
            const maxTime = def.defaultMaxTime;
            const data = stepsData[def.id] ?? {};
            // When job is done all steps are completed; otherwise normal logic
            const isCurrent = !jobDone && i === currentIdx;
            const isCompleted = jobDone || i < currentIdx;

            let startedAt: number | null = data.startedAt ?? null;
            const completedAt: number | null = data.completedAt ?? null;

            // For the current step, use stepStartTime from navData if no stepsData entry
            if (isCurrent && !startedAt) {
                startedAt = this.navData.stepStartTime ?? null;
            }

            let actualSeconds: number | null = null;
            if (isCompleted && startedAt && completedAt) {
                actualSeconds = Math.floor((completedAt - startedAt) / 1000);
            } else if (isCurrent && startedAt) {
                actualSeconds = Math.floor((now - startedAt) / 1000);
            }

            const isOver = actualSeconds !== null && actualSeconds > maxTime;
            const overBySeconds = isOver ? actualSeconds - maxTime : 0;

            return {
                id: def.id, title: def.title, maxTime, startedAt, completedAt,
                actualSeconds, isCurrent, isCompleted, isOver, overBySeconds,
            };
        });

        const currentStep = jobDone ? null : stepTimings[currentIdx];
        const totalMaxSeconds = stepTimings.reduce((s, t) => s + t.maxTime, 0);
        const totalElapsed = stepTimings.reduce((s, t) => s + (t.actualSeconds ?? 0), 0);
        const totalIsOver = totalElapsed > totalMaxSeconds;

        // Projected total: actual so far + remaining steps at their max time
        const remainingMaxSeconds = stepTimings
            .filter(t => !t.isCompleted && !t.isCurrent)
            .reduce((s, t) => s + t.maxTime, 0);
        const projectedTotalSeconds = totalElapsed + remainingMaxSeconds;
        const projectedDeltaSeconds = projectedTotalSeconds - totalMaxSeconds;
        // Clock time when the job is projected to finish = now + remaining max seconds
        const projectedFinishTime = now + remainingMaxSeconds * 1000;

        this.status = {
            currentStepId: this.navigatorCurrentStep,
            currentStepTitle: currentStep?.title ?? (jobDone ? 'Completed' : this.navigatorCurrentStep),
            currentStepMaxSeconds: currentStep?.maxTime ?? 0,
            currentStepElapsedSeconds: currentStep?.actualSeconds ?? 0,
            currentStepIsOver: currentStep?.isOver ?? false,
            currentStepOverBySeconds: currentStep?.overBySeconds ?? 0,
            totalMaxSeconds,
            totalElapsedSeconds: totalElapsed,
            totalIsOver,
            totalOverBySeconds: totalIsOver ? totalElapsed - totalMaxSeconds : 0,
            steps: stepTimings,
            projectedTotalSeconds,
            projectedDeltaSeconds,
            projectedFinishTime,
        };
    }

    formatDuration = formatDuration;
}
